import type { ConfigurationWriteError } from "@dealer-center/ddc-pages-configuration-interface";

/**
 * The single request boundary for the Configuration BFF read and write endpoints.
 *
 * This is also the **only** place the unvalidated cast to a payload type happens
 * (my-info-ui/contracts/fetch-hooks.md). Callers get a typed result; exactly one function knows
 * the payload was never checked at runtime.
 */

export const CONFIGURATION_QUERY_KEY_BASE = "ddc-pages-configuration";

const BASE_PATH = "/landing/nxlambda/configuration";

export interface ConfigurationRequestOptions {
  /**
   * The BFF resource segment — `userinfo`, `accountaccess`, `featureaccess`, `loginhistory`,
   * `userlist`, `resetpassword`, `user`.
   */
  resource: string;
  /**
   * The subject of the read, as a **path segment**. Omit it for a read whose subject is the
   * **account** rather than a user — `userlist` is the only such resource today.
   *
   * **Do not pass a dummy or empty value to satisfy this.** An empty string yields a trailing `/`
   * that the BFF would dispatch as a `resourceId`, and a placeholder is a lie in the URL. Omitting
   * the property is the supported way to express "no user subject".
   */
  userId?: string;
  accountId: string;
  /**
   * Extra query params appended after `accountId`. Entries with `undefined` are omitted, so a
   * caller can pass an optional param without branching. Values are URL-encoded.
   */
  params?: Record<string, string | number | undefined>;
}

/**
 * Builds the BFF URL in one of two forms, depending on whether the read has a user subject:
 *
 * ```text
 * userId given:   /landing/nxlambda/configuration/{resource}/{userId}?accountId={accountId}&…
 * userId omitted: /landing/nxlambda/configuration/{resource}?accountId={accountId}&…
 * ```
 *
 * `accountId` is always present and always a query param: the BFF returns 400 without it.
 *
 * **The segment-less form needs no API Gateway work.** `sls.yml` already declares
 * `GET /configuration/{resource}` alongside `GET /configuration/{resource}/{resourceId}`, so both
 * shapes are already routable.
 */
export const buildConfigurationUrl = ({
  resource,
  userId,
  accountId,
  params
}: ConfigurationRequestOptions) => {
  const search = new URLSearchParams({ accountId });
  Object.keys(params ?? {}).forEach(key => {
    const value = params?.[key];
    if (value !== undefined) {
      search.append(key, String(value));
    }
  });

  /*
   * The segment is omitted **entirely** when there is no `userId` — not emitted empty. A trailing
   * `/` would be dispatched by the BFF as a `resourceId`, which is the failure this branch exists
   * to avoid rather than a cosmetic difference.
   *
   * **Falsy, not `=== undefined`.** The property's own doc comment says "do not pass a dummy or
   * empty value to satisfy this", and `userId: ""` is type-legal — so a `=== undefined` test left the
   * documented invariant as a request to the caller rather than something this builder holds. `""`
   * now takes the same branch as an omitted value, which is the only reading that cannot produce the
   * trailing slash (tightened in review 2026-09-22).
   */
  const subjectSegment = userId ? `/${encodeURIComponent(userId)}` : "";

  return `${BASE_PATH}/${resource}${subjectSegment}?${search.toString()}`;
};

/**
 * Performs the request and throws on any non-2xx so React Query's `isError` drives the tab's
 * failed state. No sentinel, no partial payload — a failure must never render as data
 * (FR-048, SC-010).
 */
export const configurationRequest = async <TPayload>(
  options: ConfigurationRequestOptions
): Promise<TPayload> => {
  const url = buildConfigurationUrl(options);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Configuration request failed for "${options.resource}": ${response.status} ${response.statusText}`
    );
  }

  // The one unvalidated cast. See the file header and interface/ payload-type comments.
  return (await response.json()) as TPayload;
};

/** `reason` is absent when the failure did not come from the write handler (403, API Gateway, expired login). */
export class ConfigurationWriteRequestError<
  TReason extends string = string
> extends Error {
  readonly status: number;
  readonly reason?: TReason;

  constructor(status: number, message: string, reason?: TReason) {
    super(message);
    this.name = "ConfigurationWriteRequestError";
    this.status = status;
    this.reason = reason;
  }
}

const toWriteError = async <TReason extends string>(
  resource: string,
  response: Response
): Promise<ConfigurationWriteRequestError<TReason>> => {
  const fallback = `Configuration write failed for "${resource}": ${response.status} ${response.statusText}`;

  try {
    const parsed = (await response.json()) as Partial<
      ConfigurationWriteError<TReason>
    >;

    return new ConfigurationWriteRequestError<TReason>(
      response.status,
      typeof parsed?.message === "string" && parsed.message
        ? parsed.message
        : fallback,
      typeof parsed?.reason === "string" ? parsed.reason : undefined
    );
  } catch {
    return new ConfigurationWriteRequestError<TReason>(
      response.status,
      fallback
    );
  }
};

/**
 * Throws `ConfigurationWriteRequestError` on any non-2xx. `body` may hold a password: keep it out of
 * the URL, headers and logs.
 */
export const configurationWriteRequest = async <TReason extends string>(
  options: ConfigurationRequestOptions,
  body: unknown,
  method: "PUT" | "DELETE" = "PUT"
): Promise<void> => {
  const response = await fetch(
    buildConfigurationUrl(options),
    method === "DELETE"
      ? { method }
      : {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body)
        }
  );

  if (!response.ok) {
    throw await toWriteError<TReason>(options.resource, response);
  }
};
